import { useLayoutEffect, useMemo, useRef } from 'react';

interface HeadMeta {
  name?: string;
  property?: string;
  content: string;
  [key: string]: string | undefined;
}

interface HeadLink {
  rel: string;
  href: string;
  [key: string]: string | undefined;
}

interface HeadScript {
  type?: string;
  children: string;
  [key: string]: string | undefined;
}

interface HeadProps {
  title?: string;
  metas?: HeadMeta[];
  links?: HeadLink[];
  scripts?: HeadScript[];
}

export default function Head({ title, metas = [], links = [], scripts = [] }: HeadProps) {
  const instanceId = useRef<string>('');
  if (!instanceId.current) {
    instanceId.current = `head-${Math.random().toString(36).slice(2, 11)}`;
  }
  const id = instanceId.current;

  // Stable signature so the effect only re-runs when the actual content changes,
  // not on every parent re-render (booking page passes inline arrays).
  const signature = useMemo(
    () => JSON.stringify({ title, metas, links, scripts }),
    [title, metas, links, scripts],
  );

  useLayoutEffect(() => {
    const head = document.head;

    if (title !== undefined) {
      document.title = title;
    }

    metas.forEach((meta) => {
      const el = document.createElement('meta');
      el.setAttribute('data-head-id', id);
      Object.entries(meta).forEach(([key, value]) => {
        if (value !== undefined) el.setAttribute(key, value);
      });
      head.appendChild(el);
    });

    links.forEach((link) => {
      const el = document.createElement('link');
      el.setAttribute('data-head-id', id);
      Object.entries(link).forEach(([key, value]) => {
        if (value !== undefined) el.setAttribute(key, value);
      });
      head.appendChild(el);
    });

    scripts.forEach((script) => {
      const el = document.createElement('script');
      el.setAttribute('data-head-id', id);
      if (script.type) el.setAttribute('type', script.type);
      Object.entries(script).forEach(([key, value]) => {
        if (key !== 'children' && key !== 'type' && value !== undefined) {
          el.setAttribute(key, value);
        }
      });
      el.textContent = script.children;
      head.appendChild(el);
    });

    return () => {
      document.querySelectorAll(`[data-head-id="${id}"]`).forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, id]);

  return null;
}